// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module FadeIn
 * @package @geenius/analytics/solidjs-css
 * @description Provides a local SolidJS animation wrapper so the vanilla-CSS
 * variant keeps lightweight entrance motion without depending on an
 * out-of-matrix runtime package.
 */

import { type Component, type JSX, splitProps } from "solid-js";
import { cx } from "./cx";

export interface FadeInProps extends JSX.HTMLAttributes<HTMLDivElement> {
  delay?: number;
}

function createDelayStyle(
  style: JSX.CSSProperties | string | undefined,
  delay: number | undefined,
): JSX.CSSProperties | string | undefined {
  if (!delay) {
    return style;
  }

  if (typeof style === "string") {
    return `${style}; animation-delay: ${delay}ms`;
  }

  return {
    ...style,
    "animation-delay": `${delay}ms`,
  };
}

/**
 * Wraps SolidJS CSS content in the package-owned fade-in class.
 *
 * @param props Standard div attributes plus an optional animation delay.
 * @returns A wrapper element using the Analytics CSS namespace.
 */
export const FadeIn: Component<FadeInProps> = (props) => {
  const [local, rest] = splitProps(props, [
    "children",
    "class",
    "delay",
    "style",
  ]);

  return (
    <div
      {...rest}
      class={cx("gn-analytics-fade-in", local.class)}
      style={createDelayStyle(local.style, local.delay)}
    >
      {local.children}
    </div>
  );
};
